import PincodeField, { pincodeLocation, emptyPincodeLocation } from '@/components/PincodeField';
import React, { useState, useEffect, useRef } from 'react';
import api, { fileUrl } from '@/api';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';
import { Upload, Building, MapPin, Briefcase, Vault, Plus, Trash, PencilSimple, Check, X } from '@phosphor-icons/react';
import ThemeToggle from '../../components/ThemeToggle';
import UserProfileCard from '../../components/UserProfileCard';
import { SECTORS } from '@/utils/sector_options';

const VCProfile = ({ user, incubator = false }) => {
  const { formatCompact } = useCurrency();
  const [locationReady,setLocationReady]=useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [fund, setFund] = useState(null);
  const [managedFunds, setManagedFunds] = useState([]);
  const [showAddFund, setShowAddFund] = useState(false);
  const [newFund, setNewFund] = useState({ name: '', vintage_year: new Date().getFullYear(), fund_size_cr: '' });
  const [editingFundId, setEditingFundId] = useState(null);
  const [editFundData, setEditFundData] = useState({ name: '', vintage_year: '', fund_size_cr: '' });
  const [formData, setFormData] = useState({
    fund_name: '',
    fund_size_cr: '',
    preferred_sectors: [],
    preferred_stages: [],
    thesis_description: '',
    website: '',
    pincode:'',hq_state:'',hq_country:'',hq_location:'',hq_city: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchManagedFunds();
  }, []);

  const fetchManagedFunds = async () => {
    try {
      const res = await api.get('/vc/managed-funds');
      setManagedFunds(res.data);
    } catch (err) { console.error(err); }
  };

  const handleCreateFund = async () => {
    if (!newFund.name.trim()) { toast.error('Fund name is required'); return; }
    if (!newFund.vintage_year) { toast.error('Vintage year is required'); return; }
    try {
      await api.post('/vc/managed-funds', {
        name: newFund.name.trim(),
        vintage_year: parseInt(newFund.vintage_year),
        fund_size_cr: newFund.fund_size_cr ? parseFloat(newFund.fund_size_cr) : null,
      });
      toast.success('Fund added!');
      setShowAddFund(false);
      setNewFund({ name: '', vintage_year: new Date().getFullYear(), fund_size_cr: '' });
      fetchManagedFunds();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add fund'); }
  };

  const handleStartEditFund = (mf) => {
    setEditingFundId(mf.id);
    setEditFundData({ name: mf.name, vintage_year: mf.vintage_year, fund_size_cr: mf.fund_size_cr || '' });
  };

  const handleSaveEditFund = async (id) => {
    try {
      await api.put(`/vc/managed-funds/${id}`, {
        name: editFundData.name.trim(),
        vintage_year: parseInt(editFundData.vintage_year),
        fund_size_cr: editFundData.fund_size_cr ? parseFloat(editFundData.fund_size_cr) : null,
      });
      toast.success('Fund updated!');
      setEditingFundId(null);
      fetchManagedFunds();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteFund = async (id, name) => {
    if (!confirm(`Delete fund "${name}"? Investments tagged to this fund must be moved first.`)) return;
    try {
      await api.delete(`/vc/managed-funds/${id}`);
      toast.success('Fund deleted');
      fetchManagedFunds();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const fetchProfile = async () => {
    try {
      const res = await api.get('/vc/fund');
      const f = res.data;
      setFund(f);
      setFormData({
        fund_name: f.fund_name || '',
        fund_size_cr: f.fund_size_cr || '',
        preferred_sectors: f.preferred_sectors || [],
        preferred_stages: f.preferred_stages || [],
        thesis_description: f.thesis_description || '',
        website: f.website || '',
        hq_city:f.hq_city||'',pincode:f.pincode||'',hq_state:f.hq_state||'',hq_country:f.hq_country||'',hq_location:f.hq_location||''
      });
    } catch (err) {
      console.error('Failed to fetch VC profile', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if ((formData.pincode || formData.hq_city !== (fund?.hq_city||'') || formData.pincode !== (fund?.pincode||'')) && !locationReady) { toast.error('Wait for a valid pincode location before saving'); return; }
    if (!formData.pincode && formData.hq_city.trim() && !/^[A-Za-z ]+$/.test(formData.hq_city.trim())) {
      toast.error('HQ city may contain letters and spaces only');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/vc/fund', formData);
      toast.success('Fund profile updated!');
      fetchProfile();
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }

    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/files/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await api.patch('/vc/fund', { logo_url: res.data.file_id });
      toast.success('Logo updated!');
      fetchProfile();
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const toggleSector = (sector) => {
    setFormData(prev => {
      if (prev.preferred_sectors.includes(sector)) {
        return { ...prev, preferred_sectors: prev.preferred_sectors.filter(s => s !== sector) };
      }
      if (prev.preferred_sectors.length >= 6) {
        toast.error('Select up to 6 preferred sectors');
        return prev;
      }
      return { ...prev, preferred_sectors: [...prev.preferred_sectors, sector] };
    });
  };

  const toggleStage = (stage) => {
    setFormData(prev => ({
      ...prev,
      preferred_stages: prev.preferred_stages.includes(stage)
        ? prev.preferred_stages.filter(s => s !== stage)
        : [...prev.preferred_stages, stage]
    }));
  };

  const logoUrl = fund?.logo_url ? fileUrl(fund.logo_url) : null;

  if (loading) {
    return (
      <div className="p-6"><div className="max-w-4xl mx-auto text-center py-20"><div className="text-text-secondary">Loading profile...</div></div></div>
    );
  }

  const STAGES = ['Bootstrapped','Pre-seed','Seed','Pre-Series A','Series A','Series B'];

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-text-primary mb-2">Fund Profile</h1>
          <p className="text-text-secondary text-[13px]">Manage your fund information and investment thesis.</p>
        </div>

        <UserProfileCard user={user} accent={incubator ? "#d8bb80" : "#6366f1"} />

        {/* Logo */}
        <div className="bg-surface border border-border rounded-[10px] p-6 mb-6" data-testid="logo-section">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">{incubator ? 'Incubator logo' : 'Fund Logo'}</h3>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-lg overflow-hidden flex items-center justify-center bg-primary/10 border border-border">
              {logoUrl ? (
                <img src={logoUrl} alt="Fund logo" className="w-full h-full object-cover" data-testid="logo-image" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : null}
              <span className="text-primary font-mono font-bold text-3xl" style={{ display: logoUrl ? 'none' : 'flex' }} data-testid="logo-fallback">
                {formData.fund_name?.substring(0, 2).toUpperCase() || 'VC'}
              </span>
            </div>
            <div>
              <label className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg cursor-pointer hover:bg-primary/90 transition-colors ${uploadingLogo ? 'opacity-50 pointer-events-none' : ''}`}>
                <Upload size={16} weight="bold" />
                <span className="text-sm font-medium">{uploadingLogo ? 'Uploading...' : 'Upload logo'}</span>
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload" disabled={uploadingLogo} />
              </label>
              <p className="text-[11px] text-text-muted mt-2">PNG, JPG, WebP up to 5MB</p>
            </div>
          </div>
        </div>

        {/* Fund Info */}
        <div className="bg-surface border border-border rounded-[10px] p-6 mb-6" data-testid="fund-info-section">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4 flex items-center gap-2">
            <Building size={20} weight="bold" />
            {incubator ? 'Incubator information' : 'Fund Information'}
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">{incubator ? 'Incubator name *' : 'Fund Name *'}</label>
                <input type="text" value={formData.fund_name} onChange={(e) => setFormData({ ...formData, fund_name: e.target.value })} className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary" data-testid="fund-name-input" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Fund Size (₹ Cr)</label>
                <input type="number" value={formData.fund_size_cr} onChange={(e) => setFormData({ ...formData, fund_size_cr: e.target.value })} className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary font-mono focus:outline-none focus:border-primary" placeholder="e.g. 200" data-testid="fund-size-input" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Investment Thesis</label>
              <textarea value={formData.thesis_description} onChange={(e) => setFormData({ ...formData, thesis_description: e.target.value })} className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary" rows="3" placeholder="Describe your investment focus..." data-testid="thesis-input" />
            </div>
          </div>
        </div>

        {/* Investment Preferences */}
        <div className="bg-surface border border-border rounded-[10px] p-6 mb-6" data-testid="preferences-section">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4 flex items-center gap-2">
            <Briefcase size={20} weight="bold" />
            Investment Preferences
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Preferred Sectors (select up to 6)</label>
              <div className="flex flex-wrap gap-2">
                {SECTORS.map(s => (
                  <button key={s} type="button" onClick={() => toggleSector(s)} className={`px-3 py-1.5 rounded-lg border text-[12px] transition-all ${formData.preferred_sectors.includes(s) ? 'bg-primary border-primary text-white' : 'bg-background border-border text-text-secondary hover:border-primary/50'}`} data-testid={`sector-${s.toLowerCase().replace(/[\s\/&]+/g, '-')}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Preferred Stages (select all)</label>
              <div className="flex flex-wrap gap-2">
                {STAGES.map(s => (
                  <button key={s} type="button" onClick={() => toggleStage(s)} className={`px-3 py-1.5 rounded-lg border text-[12px] transition-all ${formData.preferred_stages.includes(s) ? 'bg-primary border-primary text-white' : 'bg-background border-border text-text-secondary hover:border-primary/50'}`} data-testid={`stage-${s.toLowerCase().replace(/\s+/g, '-')}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-surface border border-border rounded-[10px] p-6 mb-6" data-testid="location-section">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4 flex items-center gap-2">
            <MapPin size={20} weight="bold" />
            Location
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <PincodeField label="Headquarters pincode" value={formData.pincode} location={formData.hq_location||formData.hq_city} onChange={pin=>setFormData(current=>({...current,...emptyPincodeLocation(pin)}))} onResolve={location=>setFormData(current=>({...current,...pincodeLocation(location)}))} onStatusChange={setLocationReady} testId="city-select" disabled={saving}/>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Website</label>
              <input type="url" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary" placeholder="https://yourfund.com" data-testid="website-input" />
            </div>
          </div>
        </div>

        {/* Managed Funds (multiple funds per VC firm) */}
        <div className="bg-surface border border-border rounded-[10px] p-6 mb-6" data-testid="managed-funds-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg text-text-primary flex items-center gap-2">
              <Vault size={20} weight="bold" />
              Managed Funds
            </h3>
            {!showAddFund && (
              <button onClick={() => setShowAddFund(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm hover:bg-primary/20 transition-colors" data-testid="add-fund-btn">
                <Plus size={14} weight="bold" /> Add Fund
              </button>
            )}
          </div>
          <p className="text-[12px] text-text-muted mb-4">Add each fund vehicle you operate (e.g., "Indus Fund I — 2020 — Rs.500Cr"). You'll tag investments to a specific fund when sending invites.</p>

          {showAddFund && (
            <div className="bg-background rounded-lg p-4 mb-3 border border-primary/20" data-testid="add-fund-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Fund Name *</label>
                  <input type="text" value={newFund.name} onChange={e => setNewFund({ ...newFund, name: e.target.value })} placeholder="e.g. Climate Fund II" className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="new-fund-name" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Vintage Year *</label>
                  <input type="number" min={1990} max={2050} value={newFund.vintage_year} onChange={e => setNewFund({ ...newFund, vintage_year: e.target.value })} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-primary font-mono focus:outline-none focus:border-primary" data-testid="new-fund-vintage" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Fund Size (₹ Cr)</label>
                  <input type="number" value={newFund.fund_size_cr} onChange={e => setNewFund({ ...newFund, fund_size_cr: e.target.value })} placeholder="500" className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-primary font-mono focus:outline-none focus:border-primary" data-testid="new-fund-size" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setShowAddFund(false); setNewFund({ name: '', vintage_year: new Date().getFullYear(), fund_size_cr: '' }); }} className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors" data-testid="cancel-add-fund">Cancel</button>
                <button onClick={handleCreateFund} className="px-4 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors" data-testid="save-fund-btn">Add Fund</button>
              </div>
            </div>
          )}

          {managedFunds.length === 0 && !showAddFund ? (
            <div className="text-center py-8 text-text-muted text-sm border border-dashed border-border rounded-lg">No funds yet. Click "Add Fund" to get started.</div>
          ) : (
            <div className="space-y-2">
              {managedFunds.map(mf => (
                <div key={mf.id} className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border" data-testid={`fund-${mf.id}`}>
                  {editingFundId === mf.id ? (
                    <>
                      <input type="text" value={editFundData.name} onChange={e => setEditFundData({ ...editFundData, name: e.target.value })} className="flex-1 bg-surface border border-border rounded px-2 py-1 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid={`edit-fund-name-${mf.id}`} />
                      <input type="number" value={editFundData.vintage_year} onChange={e => setEditFundData({ ...editFundData, vintage_year: e.target.value })} className="w-20 bg-surface border border-border rounded px-2 py-1 text-sm text-text-primary font-mono focus:outline-none focus:border-primary" />
                      <input type="number" value={editFundData.fund_size_cr} onChange={e => setEditFundData({ ...editFundData, fund_size_cr: e.target.value })} placeholder="Cr" className="w-20 bg-surface border border-border rounded px-2 py-1 text-sm text-text-primary font-mono focus:outline-none focus:border-primary" />
                      <button onClick={() => handleSaveEditFund(mf.id)} className="text-success hover:text-success/80" data-testid={`save-edit-${mf.id}`}><Check size={18} weight="bold" /></button>
                      <button onClick={() => setEditingFundId(null)} className="text-text-muted hover:text-text-primary"><X size={18} weight="bold" /></button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">{mf.name}</div>
                        <div className="text-[11px] text-text-muted font-mono">Vintage {mf.vintage_year}{mf.fund_size_cr ? ` · ${formatCompact(mf.fund_size_cr * 1e7)}` : ''}</div>
                      </div>
                      <button onClick={() => handleStartEditFund(mf)} className="text-text-muted hover:text-primary" data-testid={`edit-fund-${mf.id}`}><PencilSimple size={16} /></button>
                      <button onClick={() => handleDeleteFund(mf.id, mf.name)} className="text-text-muted hover:text-danger" data-testid={`delete-fund-${mf.id}`}><Trash size={16} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Platform Settings */}
        <ThemeToggle accent={incubator ? "#d8bb80" : "#6366f1"} />

        {/* Save */}
        <div className="flex justify-end gap-3 mt-6 mb-8">
          <button onClick={fetchProfile} className="px-6 py-3 bg-surface border border-border text-text-primary rounded-lg hover:border-primary/50 transition-colors" data-testid="cancel-button">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50" data-testid="save-button">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VCProfile;
